import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import { syncQueuedRepairs } from './services/localDb';
import { verisureService } from './services/api';

// Pages
import { LoginPage } from './pages/LoginPage';
import { PendingPage } from './pages/PendingPage';
import { DashboardPage } from './pages/DashboardPage';
import { ItemsPage } from './pages/ItemsPage'; 
import { InventoryPage as PartsStockPage } from './pages/InventoryPage'; // To be renamed later
import { TicketingPage as IncidentsPage } from './pages/TicketingPage'; // To be renamed later
import { SignaturePage } from './pages/SignaturePage';
import { AdminPage } from './pages/AdminPage';
import { TenantBillingPage } from './pages/TenantBillingPage';
import { ClaimsPage } from './pages/ClaimsPage';
import { ReportPage } from './pages/ReportPage';
// Verisure Pages
import { AdminVerisurePage } from './pages/verisure/AdminVerisurePage';
import { LogRepairPage } from './pages/verisure/LogRepairPage';
import { PublicItemPage } from './pages/verisure/PublicItemPage'; 
import { VerisureQrGeneratorPage } from './pages/verisure/VerisureQrGeneratorPage';
import { TechniciansPage } from './pages/verisure/TechniciansPage';
import { BlankQrManagerPage } from './pages/verisure/BlankQrManagerPage';
import { TagDevicePage } from './pages/verisure/TagDevicePage';
import { LandingPage } from './pages/LandingPage';
import { AboutPage } from './pages/AboutPage';
import { TermsPage } from './pages/legal/TermsPage';
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage';
import { ClaimsEvidencePolicyPage } from './pages/legal/ClaimsEvidencePolicyPage';
import { TechnicianPolicyPage } from './pages/legal/TechnicianPolicyPage';
import { NotFoundPage } from './pages/NotFoundPage';

// Modals & Components
import { InstallPwaPrompt } from './components/InstallPwaPrompt';
import { InbuiltQrScannerModal } from './components/verisure/InbuiltQrScannerModal';
import { CriticalAlertTicker } from './components/alerts/CriticalAlertTicker';
import { MobileBottomNav } from './components/navigation/MobileBottomNav';

// Placeholder Pages for those yet to be created (will be created in Phase 5)

const Navigation: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  if (!isAuthenticated) return null;

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const isAdmin = ['super_admin', 'master_admin'].includes(user?.role || '');
  const isTechOrStaff = ['technician', 'super_admin', 'master_admin', 'field_staff'].includes(user?.role || '');

  return (
    <>
      <header className="no-print sticky top-0 z-50 glass-navbar w-full max-w-full">
      {/* Primary Top Bar */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2 sm:gap-4">
          
          {/* Brand Lockup */}
          <div className="flex items-center gap-3 sm:gap-6 shrink-0">
            <Link to="/dashboard" className="flex items-center gap-2.5 group">
              <img src="/official.png" alt="Verisure" className="h-8 sm:h-9 w-auto object-contain transition group-hover:scale-105" />
              <div className="hidden lg:flex flex-col">
                <span className="text-xs font-black tracking-widest text-white uppercase">VERISURE</span>
                <span className="text-[10px] text-orange-400 font-semibold tracking-wider">HARDWARE REGISTRY</span>
              </div>
            </Link>

            {/* Grey.co-style Segmented Capsule for Core Navigation (Desktop) */}
            <div className="hidden xl:flex items-center glass-pill-container p-1 rounded-full">
              <Link
                to="/dashboard"
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive('/dashboard')
                    ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {t('dashboard')}
              </Link>
              <Link
                to="/items"
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive('/items') && !isActive('/items/search')
                    ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {t('items_registry')}
              </Link>
              <Link
                to="/technicians"
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive('/technicians')
                    ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                Technicians
              </Link>
              <Link
                to="/qr-generator"
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive('/qr-generator')
                    ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                QR Studio
              </Link>
              {isAdmin && (
                <Link
                  to="/admin/blank-qr"
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                    isActive('/admin/blank-qr')
                      ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                      : 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'
                  }`}
                >
                  Outlet QRs
                </Link>
              )}
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* Quick Action: Log Repair for Techs */}
            {isTechOrStaff && (
              <Link
                to="/repair/log"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-slate-200 bg-slate-900 border border-slate-700/80 hover:border-orange-500/50 hover:text-white transition"
              >
                <svg className="w-3.5 h-3.5 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
                <span>Log Repair</span>
              </Link>
            )}

            {/* Quick Scanner Action */}
            <button
              onClick={() => setIsScannerOpen(true)}
              className="relative group bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs flex items-center gap-1.5 sm:gap-2 shadow-lg shadow-orange-500/25 transition-all active:scale-95"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h4a1 1 0 010 2H5v3a1 1 0 01-2 0V4zm14-1a1 1 0 011 1v3a1 1 0 01-2 0V5h-3a1 1 0 010-2h4zM3 20a1 1 0 011-1h4a1 1 0 110 2H5v-3a1 1 0 01-2 0v4zm14 1a1 1 0 011-1v-4a1 1 0 112 0v3a1 1 0 01-1 1h-4a1 1 0 110-2h3z" />
              </svg>
              <span className="hidden xs:inline">{t('scan_qr')}</span>
              <span className="xs:hidden">Scan</span>
            </button>

            <InstallPwaPrompt />

            {/* Language Capsule */}
            <div className="relative hidden sm:block">
              <select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                aria-label="Select Language"
                className="bg-slate-900/90 border border-white/10 text-slate-300 text-xs rounded-full px-2.5 py-1.5 focus:outline-none focus:border-orange-500 font-semibold cursor-pointer"
              >
                <option value="en">EN</option>
                <option value="fr">FR</option>
                <option value="es">ES</option>
              </select>
            </div>

            {/* Plans & Billing Quick Link */}
            <Link
              to="/billing"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-slate-300 hover:text-white glass-pill-container border border-white/10 hover:border-orange-500/40 transition"
              title="View Subscription Plan & QR Quota"
            >
              <span>💳</span>
              <span>Plans &amp; Quota</span>
            </Link>

            {/* User Profile Capsule */}
            <div className="hidden sm:flex items-center gap-2.5 glass-pill-container px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
              <div className="text-left">
                <div className="text-xs font-bold text-white leading-none">{user?.name}</div>
                <div className="text-[10px] text-orange-400 font-semibold leading-tight tracking-wider uppercase mt-0.5">
                  {user?.role?.replace('_', ' ')}
                </div>
              </div>
            </div>

            {/* Logout Button (Desktop) */}
            <button
              onClick={logout}
              className="hidden sm:inline-flex text-xs font-semibold text-slate-400 hover:text-white px-3 py-1.5 rounded-full border border-white/5 hover:bg-white/5 transition"
              title="Sign Out"
            >
              {t('logout')}
            </button>

            {/* Mobile Menu / Drawer Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 rounded-full glass-pill-container text-slate-300 hover:text-white border border-white/10 active:scale-95 transition"
              aria-label="Toggle navigation menu"
              title="Toggle Menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Secondary Clean Glass Navigation Track */}
      <div className="border-t border-white/[0.06] bg-[#0c101a]/70 backdrop-blur-md px-3 sm:px-6 lg:px-8 py-1.5 w-full max-w-full min-w-0 overflow-x-auto scrollbar-none touch-pan-x flex items-center gap-1.5 sm:gap-2 text-xs flex-nowrap">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase mr-1 shrink-0">Workspace:</span>
        <Link
          to="/dashboard"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/dashboard') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          {t('dashboard')}
        </Link>
        <Link
          to="/items"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/items') && !isActive('/items/search') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          {t('items_registry')}
        </Link>
        <Link
          to="/technicians"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/technicians') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          Technicians
        </Link>
        <Link
          to="/qr-generator"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/qr-generator') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          QR Studio
        </Link>
        {isAdmin && (
          <Link
            to="/admin/blank-qr"
            className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-semibold shrink-0 ${
              isActive('/admin/blank-qr') ? 'bg-gradient-to-r from-red-500/20 via-orange-500/25 to-amber-500/20 text-orange-400 border border-orange-500/40 shadow-sm' : 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'
            }`}
          >
            Outlet Blank QRs
          </Link>
        )}
        <Link
          to="/parts"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/parts') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          {t('parts_stock')}
        </Link>
        <Link
          to="/incidents"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/incidents') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          {t('incidents')}
        </Link>
        <Link
          to="/reports"
          className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
            isActive('/reports') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          Reports
        </Link>

        {/* State Manager / Super Admin Signatures */}
        {['super_admin', 'state_manager'].includes(user?.role || '') && (
          <Link
            to="/signatures"
            className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
              isActive('/signatures') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-slate-300 hover:text-white hover:bg-white/5'
            }`}
          >
            Signatures
          </Link>
        )}

        {/* Admin Exclusive Segments */}
        {isAdmin && (
          <>
            <div className="w-px h-4 bg-white/10 mx-1 shrink-0"></div>
            <Link
              to="/claims"
              className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
                isActive('/claims') ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10'
              }`}
            >
              Claims
            </Link>
            <Link
              to="/admin"
              className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium shrink-0 ${
                isActive('/admin') ? 'bg-slate-800 text-white border border-white/20' : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              Staff Admin
            </Link>
            {user?.role === 'master_admin' && (
              <Link
                to="/billing"
                className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors font-medium flex items-center gap-1 shrink-0 ${
                  isActive('/billing') ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-emerald-400 hover:bg-emerald-500/10'
                }`}
              >
                Billing
              </Link>
            )}
          </>
        )}
      </div>

      {/* Mobile Slide-Over Navigation Drawer */}
      {isMobileMenuOpen && (
        <div className="xl:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex flex-col justify-end sm:justify-start transition-all animate-in fade-in duration-200">
          <div className="bg-[#0e1422] border-t sm:border-r sm:border-t-0 border-white/10 sm:max-w-md w-full h-[85vh] sm:h-full flex flex-col rounded-t-3xl sm:rounded-none shadow-2xl overflow-hidden">
            {/* Drawer Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></div>
                <div>
                  <div className="text-sm font-bold text-white">{user?.name}</div>
                  <div className="text-[10px] text-orange-400 font-semibold uppercase tracking-wider">
                    {user?.role?.replace('_', ' ')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Drawer Navigation Links */}
            <div className="p-4 space-y-5 overflow-y-auto flex-1">
              {/* Workspace Section */}
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 px-2">Core Registry</div>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-3 rounded-2xl flex items-center gap-2.5 text-xs font-bold transition ${
                      isActive('/dashboard') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'bg-white/5 text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    <span>🏠</span> Home
                  </Link>
                  <Link
                    to="/items"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-3 rounded-2xl flex items-center gap-2.5 text-xs font-bold transition ${
                      isActive('/items') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'bg-white/5 text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    <span>📦</span> Hardware
                  </Link>
                  <Link
                    to="/technicians"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-3 rounded-2xl flex items-center gap-2.5 text-xs font-bold transition ${
                      isActive('/technicians') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'bg-white/5 text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    <span>👨‍🔧</span> Technicians
                  </Link>
                  <Link
                    to="/qr-generator"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`p-3 rounded-2xl flex items-center gap-2.5 text-xs font-bold transition ${
                      isActive('/qr-generator') ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'bg-white/5 text-slate-200 hover:bg-white/10'
                    }`}
                  >
                    <span>🔲</span> QR Studio
                  </Link>
                </div>
              </div>

              {/* Operations Section */}
              <div>
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 px-2">Operations & Logistics</div>
                <div className="space-y-1.5">
                  <Link
                    to="/repair/log"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2.5"><span>🛠️</span> Log Certified Repair</span>
                    <span className="text-[10px] text-orange-400 font-normal">Tamper-Proof</span>
                  </Link>
                  <Link
                    to="/parts"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2.5"><span>⚙️</span> Parts & Stock Inventory</span>
                  </Link>
                  <Link
                    to="/incidents"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2.5"><span>🚨</span> Incidents & Tickets</span>
                  </Link>
                  <Link
                    to="/reports"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2.5"><span>📄</span> Operational Reports</span>
                  </Link>
                  {['super_admin', 'state_manager'].includes(user?.role || '') && (
                    <Link
                      to="/signatures"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                    >
                      <span className="flex items-center gap-2.5"><span>✍️</span> Digital Signatures</span>
                    </Link>
                  )}
                  <Link
                    to="/billing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-3 rounded-2xl flex items-center justify-between text-xs font-bold bg-white/5 text-slate-200 hover:bg-white/10 transition"
                  >
                    <span className="flex items-center gap-2.5"><span>💳</span> Plans &amp; Subscriptions</span>
                    <span className="text-[10px] text-emerald-400 font-mono">Freemium</span>
                  </Link>
                </div>
              </div>

              {/* Admin Mode Exclusive Section */}
              {isAdmin && (
                <div className="p-3.5 rounded-2xl bg-gradient-to-br from-orange-500/10 via-red-500/10 to-amber-500/10 border border-orange-500/30">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-[11px] font-black uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
                      Admin Command Suite
                    </span>
                    <span className="text-[10px] bg-orange-500/20 text-orange-300 font-bold px-2 py-0.5 rounded-full border border-orange-500/30">
                      ADMIN
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <Link
                      to="/admin"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2.5 rounded-xl flex items-center justify-between text-xs font-bold bg-[#0d1320] text-white hover:bg-white/10 border border-white/5 transition"
                    >
                      <span className="flex items-center gap-2"><span>🛡️</span> Staff & Roles Management</span>
                      <span className="text-[10px] text-orange-400 font-mono">/admin</span>
                    </Link>
                    <Link
                      to="/admin/blank-qr"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2.5 rounded-xl flex items-center justify-between text-xs font-bold bg-[#0d1320] text-white hover:bg-white/10 border border-white/5 transition"
                    >
                      <span className="flex items-center gap-2"><span>🏷️</span> Blank Outlet QR Studio</span>
                      <span className="text-[10px] text-orange-400 font-mono">Outlets</span>
                    </Link>
                    <Link
                      to="/claims"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2.5 rounded-xl flex items-center justify-between text-xs font-bold bg-[#0d1320] text-white hover:bg-white/10 border border-white/5 transition"
                    >
                      <span className="flex items-center gap-2"><span>📑</span> Insurance Claims Evidence</span>
                      <span className="text-[10px] text-amber-400 font-mono">Claims</span>
                    </Link>
                    {user?.role === 'master_admin' && (
                      <Link
                        to="/billing"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2.5 rounded-xl flex items-center justify-between text-xs font-bold bg-[#0d1320] text-white hover:bg-white/10 border border-white/5 transition"
                      >
                        <span className="flex items-center gap-2"><span>💳</span> Tenant Billing & Subscriptions</span>
                        <span className="text-[10px] text-emerald-400 font-mono">Billing</span>
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Language & Utilities */}
              <div className="pt-2 flex items-center justify-between border-t border-white/10">
                <span className="text-xs text-slate-400 font-semibold">Language</span>
                <select
                  value={i18n.language}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                  className="bg-slate-900 border border-white/10 text-slate-200 text-xs rounded-xl px-3 py-1.5 focus:outline-none"
                >
                  <option value="en">English (EN)</option>
                  <option value="fr">Français (FR)</option>
                  <option value="es">Español (ES)</option>
                </select>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-white/10 bg-[#090d16]">
              <button
                onClick={() => { setIsMobileMenuOpen(false); logout(); }}
                className="w-full py-3 rounded-2xl bg-white/5 hover:bg-red-500/20 text-red-400 font-bold text-xs flex items-center justify-center gap-2 border border-red-500/20 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out of Account
              </button>
            </div>
          </div>
        </div>
      )}
      </header>

      <InbuiltQrScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
      />

      <MobileBottomNav onOpenScanner={() => setIsScannerOpen(true)} />
    </>
  );
};

function App() {
  React.useEffect(() => {
    const handleOnline = () => {
      syncQueuedRepairs(verisureService.logRepair);
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return (
    <Router>
      <Toaster position="top-right" />
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/claims-policy" element={<ClaimsEvidencePolicyPage />} />
          <Route path="/technician-policy" element={<TechnicianPolicyPage />} />
          <Route path="/item/:assetId" element={<PublicItemPage />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected Area */}
          <Route path="/*" element={
            <div className="min-h-screen bg-slate-950 text-slate-200 pb-28 md:pb-8 min-w-0 max-w-full overflow-x-hidden">
              <CriticalAlertTicker />
              <Navigation />
              <Routes>
                <Route
                  path="/dashboard"
                  element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
                />
                <Route
                  path="/pending"
                  element={<ProtectedRoute><PendingPage /></ProtectedRoute>}
                />
                <Route
                  path="/items"
                  element={<ProtectedRoute><ItemsPage /></ProtectedRoute>}
                />
                <Route
                  path="/technicians"
                  element={<ProtectedRoute><TechniciansPage /></ProtectedRoute>}
                />
                <Route
                  path="/qr-generator"
                  element={<ProtectedRoute><VerisureQrGeneratorPage /></ProtectedRoute>}
                />
                <Route
                  path="/qr-card"
                  element={<ProtectedRoute><VerisureQrGeneratorPage /></ProtectedRoute>}
                />
                <Route
                  path="/verisure/qr-card"
                  element={<ProtectedRoute><VerisureQrGeneratorPage /></ProtectedRoute>}
                />
                <Route
                  path="/tag/:tagId"
                  element={<ProtectedRoute><TagDevicePage /></ProtectedRoute>}
                />
                <Route
                  path="/tag"
                  element={<ProtectedRoute><TagDevicePage /></ProtectedRoute>}
                />
                <Route
                  path="/parts"
                  element={<ProtectedRoute requiredRoles={['storekeeper', 'state_manager']}><PartsStockPage /></ProtectedRoute>}
                />
                <Route
                  path="/incidents"
                  element={<ProtectedRoute requiredRoles={['state_manager', 'super_admin']}><IncidentsPage /></ProtectedRoute>}
                />
                <Route
                  path="/reports"
                  element={<ProtectedRoute requiredRoles={['state_manager', 'super_admin', 'master_admin']}><ReportPage /></ProtectedRoute>}
                />
                <Route
                  path="/signatures"
                  element={<ProtectedRoute requiredRoles={['state_manager', 'super_admin']}><SignaturePage /></ProtectedRoute>}
                />
                <Route
                  path="/admin"
                  element={<ProtectedRoute requiredRoles={['super_admin', 'master_admin']}><AdminPage /></ProtectedRoute>}
                />
                <Route
                  path="/claims"
                  element={<ProtectedRoute requiredRoles={['super_admin', 'master_admin', 'equipment_owner']}><ClaimsPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/verisure"
                  element={<ProtectedRoute requiredRoles={['super_admin', 'master_admin']}><AdminVerisurePage /></ProtectedRoute>}
                />
                <Route
                  path="/admin/blank-qr"
                  element={<ProtectedRoute requiredRoles={['super_admin', 'master_admin']}><BlankQrManagerPage /></ProtectedRoute>}
                />
                <Route
                  path="/repair/log/:assetId?"
                  element={<ProtectedRoute requiredRoles={['technician', 'super_admin', 'master_admin', 'field_staff']}><LogRepairPage /></ProtectedRoute>}
                />
                <Route
                  path="/billing"
                  element={<ProtectedRoute><TenantBillingPage /></ProtectedRoute>}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          } />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
